import goitaIcon from "@assets/images/goita.svg";
import { LEGAL_LINKS } from "@constants/legalLinks";
import ROUTES from "@constants/routing";
import { useUser } from "@context/User/UserContext";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography
} from "@mui/material";
import { logoutFromARA } from "@services/user/UserService";
import {
  pillButtonContained,
  pillButtonOutlined,
  profileDialogMessage,
  profileDialogStyles,
  profileDialogTitle,
  profileFooterLink,
  profileIconButton,
  profileIconButtonTransparent,
  profileTypographyBase,
  profileTypographyEmail,
  profileTypographySecondary,
  profileTypographyTitle
} from "@theme/styles";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LegalConditionsDialog from "../LegalConditions/LegalConditionsDialog";
import LanguageDialog from "./LanguageDialog";

// App version - update this when releasing new versions
const APP_VERSION = "2.1.0";

export interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const languages = [
  { code: "ca" as const, label: "Català", short: "CAT" },
  { code: "es" as const, label: "Castellano", short: "CAS" },
  { code: "en" as const, label: "English", short: "ENG" }
];

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  if (!user) return null;

  const currentLanguage = (i18n.language as "es" | "en" | "ca") || "ca";

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            maxWidth: { xs: "100%", sm: "85%" },
            boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.1)",
            bgcolor: (theme) => theme.palette.background.paper
          }
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: (theme) => theme.palette.background.paper,
            paddingTop: {
              xs: "max(env(safe-area-inset-top, 0px), 40px)",
              sm: 0
            }
          }}
        >
          {/* Header with language selector and close button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Typography
                onClick={() => setLanguageMenuOpen(true)}
                sx={{
                  fontFamily: (theme) => theme.typography.fontFamily,
                  fontWeight: "bold",
                  fontSize: "12px",
                  lineHeight: "14px",
                  letterSpacing: "0px",
                  textTransform: "uppercase",
                  color: (theme) => theme.palette.customColors.textPrimary,
                  cursor: "pointer"
                }}
              >
                {languages.find((l) => l.code === currentLanguage)?.short ||
                  "CAT"}
              </Typography>
              <IconButton
                onClick={() => setLanguageMenuOpen(true)}
                size="small"
                sx={{
                  p: 0,
                  ...profileIconButtonTransparent()
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </Box>
            <IconButton onClick={onClose} size="small" sx={profileIconButton()}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Box>

          {/* User Profile Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 3
            }}
          >
            <Avatar
              src={user.avatarUrl}
              alt={user.name}
              sx={{
                width: 80,
                height: 80,
                mb: 2
              }}
            />
            <Typography sx={profileTypographyTitle()}>{user.name}</Typography>
            <Typography sx={profileTypographyEmail()}>{user.email}</Typography>
            <Typography
              component="button"
              onClick={() => setOpenLogoutModal(true)}
              sx={{
                ...profileTypographyBase(
                  "14px",
                  "18px",
                  (theme) => theme.palette.customColors.textPrimary
                ),
                textAlign: "center",
                mt: 2,
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": {
                  color: (theme) => theme.palette.customColors.textSecondary
                }
              }}
            >
              {t("navbar.logout.title", "Cerrar sesión")}
            </Typography>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Footer with app info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 4, // 32px
              pr: 2, // 16px
              pb: 4, // 32px
              pl: 2, // 16px
              gap: 1 // 8px
            }}
          >
            <Box
              component="img"
              src={goitaIcon}
              alt="GOITA"
              sx={{
                width: "75.66px",
                height: "19.07px",
                mb: 1,
                filter:
                  "brightness(0) saturate(100%) invert(33%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)"
                // This filter converts the SVG to #545454 color
              }}
            />
            <Typography
              sx={{
                ...profileTypographySecondary("14px", "120%"),
                mb: 0
              }}
            >
              GOITA Barcelona v{APP_VERSION}
              <br />
              {t("navbar.footer.initiative", "Es una iniciativa del diari ara")}
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                px: 0,
                py: 0,
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.background.paper,
                width: "100%"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 0.5,
                  alignItems: "center",
                  whiteSpace: "nowrap"
                }}
              >
                {[
                  {
                    key: "legalNotice",
                    labelKey: "navbar.footer.links.legal_notice"
                  },
                  {
                    key: "conditions",
                    labelKey: "navbar.footer.links.conditions"
                  },
                  {
                    key: "privacy",
                    labelKey: "navbar.footer.links.privacy"
                  },
                  {
                    key: "chatbot",
                    labelKey: "navbar.footer.links.chatbot"
                  }
                ].map((item, index, array) => {
                  const href =
                    LEGAL_LINKS[currentLanguage][
                      item.key as keyof (typeof LEGAL_LINKS)["ca"]
                    ];

                  const isLegalNotice = item.key === "legalNotice";
                  const isLast = index === array.length - 1;

                  return (
                    <React.Fragment key={item.key}>
                      <Typography
                        component={isLegalNotice ? "button" : "a"}
                        href={isLegalNotice ? undefined : href}
                        onClick={
                          isLegalNotice
                            ? () => setLegalDialogOpen(true)
                            : undefined
                        }
                        target={
                          !isLegalNotice && href !== "#" ? "_blank" : undefined
                        }
                        rel={
                          !isLegalNotice && href !== "#"
                            ? "noopener noreferrer"
                            : undefined
                        }
                        sx={{
                          ...profileFooterLink(),
                          "&:hover": {
                            textDecoration:
                              !isLegalNotice && href !== "#"
                                ? "underline"
                                : "none"
                          }
                        }}
                      >
                        {t(item.labelKey)}
                      </Typography>
                      {!isLast && (
                        <Typography
                          component="span"
                          sx={{
                            ...profileTypographyBase("14px", "15px", "#444444"),
                            display: "inline",
                            height: "15px",
                            textAlign: "center"
                          }}
                        >
                          ·
                        </Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <LanguageDialog
        open={languageMenuOpen}
        onClose={() => setLanguageMenuOpen(false)}
      />
      <LegalConditionsDialog
        open={legalDialogOpen}
        onAccept={() => setLegalDialogOpen(false)}
        onClose={() => setLegalDialogOpen(false)}
      />
      <Dialog
        open={openLogoutModal}
        onClose={() => setOpenLogoutModal(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: profileDialogStyles()
        }}
      >
        <DialogTitle sx={profileDialogTitle()}>
          {t("navbar.logout.title", "Cerrar sesión")}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={profileDialogMessage()}>
            {t(
              "navbar.logout.message",
              "¿Estás seguro que quieres cerrar tu sesión?"
            )}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              gap: 2
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenLogoutModal(false)}
              sx={{
                ...pillButtonOutlined("#000000"),
                px: { xs: 3, sm: 5 },
                width: { xs: "100%", sm: "auto" },
                maxWidth: 326,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              {t("navbar.logout.cancel", "Cancelar")}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                setOpenLogoutModal(false);
                // Llamar al endpoint oficial de ARA para cerrar sesión
                await logoutFromARA();
                // Redirigir a welcome (el sistema detectará que no hay sesión)
                window.location.href = ROUTES.WELCOME;
              }}
              sx={{
                ...pillButtonContained("#000000"),
                px: { xs: 3, sm: 5 },
                width: { xs: "100%", sm: "auto" },
                maxWidth: 326
              }}
            >
              {t("navbar.logout.confirm", "Confirmar")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileDrawer;
